import { Grid, IconNames, Tag, Text, Heading, useDate, Divider, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import React, { FunctionComponent, ReactNode, useEffect } from 'react'
import { Item, SubscriptionItem } from '@afterpaytouch/portal-api/types'
import { money } from '../../model'
import {
  CheckoutState,
  useAppRouter,
  setDoItemsReconcile,
  useAppDispatch,
  useCheckoutState,
  useItemsAndSubscriptions,
  useShippingOption,
  useCrossBorderTrade,
  useIsCheckoutLoadingOrUpdating,
} from '../../state'
import { Money } from '../Money'

import styles from './style.module.css'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { isInAppBrowser } from '../../utils/device'
import clsx from 'clsx'
import { isNullOrUndefined, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { OrderDetailsBreakdown } from './OrderDetailsBreakdown'
import { Money as MoneyModel } from '@afterpay/types'

interface DividerWrapperProps {
  children: ReactNode
}

// note that these are not domain models, they are specific to this component
export interface OrderLineItemModel {
  name: string
  iconName: IconNames
  quantity: number
  preorder: boolean
  deposit: MoneyModel | undefined
  estimatedShipmentDate?: string
  total?: MoneyModel
}

export interface OrderDetailsModel {
  orderLineItems: OrderLineItemModel[] | undefined
  subTotalAmount: MoneyModel
  shippingAmount?: MoneyModel
  taxAmount?: MoneyModel
  reconciles: boolean
  isPickup: boolean
}

export const formatItemQuantity = (itemQuantity: number): string => {
  return itemQuantity > 999 ? '999+' : itemQuantity.toString()
}

export const moneyToNumber = (moneyModel: MoneyModel | undefined): number => {
  return Number(moneyModel?.amount ?? 0)
}

export const mapToOrderLineItem = (item: Item | SubscriptionItem): OrderLineItemModel => {
  let total: MoneyModel | undefined
  const quantity = 'quantity' in item ? item.quantity : 1
  const preorder = 'preorder' in item ? item.preorder ?? false : false
  const deposit = 'deposit' in item ? item.deposit : undefined

  const estimatedShipmentDate = 'estimatedShipmentDate' in item && item.estimatedShipmentDate !== '' ? item.estimatedShipmentDate : undefined
  if (item.price !== undefined) {
    const amount = moneyToNumber(item.price) * quantity
    total = money(amount.toFixed(2), item.price.currency)
  }

  const isSubscriptionItem = 'billingStartAt' in item
  const iconName = isSubscriptionItem ? 'CardUpdate' : 'Afterpay'

  return {
    name: item.name,
    estimatedShipmentDate,
    preorder,
    deposit,
    iconName,
    quantity,
    total,
  }
}

export const mapToOrderDetails = (
  shippingAmount: MoneyModel | undefined,
  taxAmount: MoneyModel | undefined,
  orderAmount: MoneyModel | undefined,
  itemsAndSubscriptions: Array<Item | SubscriptionItem> = [],
  isPickup: boolean = false
): OrderDetailsModel => {
  let orderLineItems: OrderLineItemModel[] | undefined
  let reconciles = true

  const shipping = moneyToNumber(shippingAmount)
  const tax = moneyToNumber(taxAmount)
  const orderTotal = moneyToNumber(orderAmount)

  if (itemsAndSubscriptions?.length > 0) {
    orderLineItems = itemsAndSubscriptions.map((item: Item | SubscriptionItem) => mapToOrderLineItem(item))

    // the line item price is supposed to be based on the unit price of the item i.e. price multiplied by quantity. however there is no validation on the API to enforce this.
    // to avoid showing item prices that do not add up, we calculate the sum of line item prices and try to reconcile against the order total.
    const itemPricesTotal = orderLineItems.map((orderLineItem: OrderLineItemModel) => moneyToNumber(orderLineItem.total)).reduce((prev, next) => prev + next)

    // consider both tax inclusive and exclusive item prices to be flexible with tax applied at item level e.g. AU GST is item level, but US taxes may not be
    const breakdownTotalIncludingTax = itemPricesTotal + shipping + tax
    const breakdownTotalExcludingTax = itemPricesTotal + shipping

    reconciles = breakdownTotalIncludingTax === orderTotal || breakdownTotalExcludingTax === orderTotal
  }

  const zeroAmount = money('0.00', orderAmount?.currency)
  const shippingAmountOrZeroAmount = shippingAmount ?? zeroAmount
  const taxAmountOrZeroAmount = taxAmount ?? zeroAmount

  const subTotal = orderTotal - shipping - tax
  const subTotalAmount = money(subTotal.toFixed(2), orderAmount?.currency)

  return {
    orderLineItems,
    subTotalAmount,
    shippingAmount: shippingAmountOrZeroAmount,
    taxAmount: taxAmountOrZeroAmount,
    reconciles,
    isPickup,
  }
}

export const OrderDetails: FunctionComponent = () => {
  const state: CheckoutState = useCheckoutState()
  const { selectedShippingOption: nonXbtSelectedShippingOption, xbtAllShippingOptions } = useShippingOption()
  const { isCrossBorder } = useCrossBorderTrade()
  const { t } = useTranslation()
  const { smallTextSize, sectionHeadingSize, textSize } = useCheckoutSizes()
  const { locale } = useAppRouter()
  const inAppBrowser = isInAppBrowser()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { formatDate } = useDate({
    locale,
    year: '2-digit',
    month: 'short',
    day: 'numeric',
  })
  const dispatch = useAppDispatch()

  const itemsAndSubscriptions = useItemsAndSubscriptions()
  const orderAmount = state.started?.consumerAmount ?? state.checkout?.amount
  const selectedShippingOption = nonXbtSelectedShippingOption
    ? isCrossBorder
      ? xbtAllShippingOptions.find((option) => option.id === nonXbtSelectedShippingOption.id)
      : nonXbtSelectedShippingOption
    : undefined
  const shippingAmount = selectedShippingOption?.shippingAmount
  const taxAmount = selectedShippingOption?.taxAmount
  const isPickup = state.initial?.pickup ?? false
  const isBoldLabel = !isArcadeDesignLanguage

  const orderDetails = mapToOrderDetails(shippingAmount, taxAmount, orderAmount, itemsAndSubscriptions, isPickup)
  const summaryTotal = state.started?.promotion?.owedAfterDiscount ?? orderAmount

  const hasItems = (orderDetails.orderLineItems?.length ?? -1) > 0
  const hasSelectedShippingOption = selectedShippingOption !== undefined
  const shouldRenderTotal = isArcadeDesignLanguage && valueIsNotNullOrUndefined(selectedShippingOption) && hasItems && valueIsNotNullOrUndefined(summaryTotal)
  const isShippingOptionUpdating = useIsCheckoutLoadingOrUpdating()

  const renderOrderLineItem = (orderLineItem: OrderLineItemModel, index: number, loading: boolean, showValue: boolean): ReactNode => {
    // the totals do not reconcile, so we hide the item value to avoid confusing the consumer
    const hideValue = !showValue && !loading
    const orderLineItemNameStyle = clsx(styles.orderLineItemName, inAppBrowser && styles.inAppBrowser)

    return (
      <div className={styles.orderLineItemContainer} key={`order-line-item-${index}`}>
        <Grid key={`order-details-${index}`} center>
          <div className={hideValue ? undefined : styles.ellipsisContainer}>
            <MaskedElement actionName='Order Line Item'>
              <div className={styles.orderLineItemPreorderWrapper}>
                <Text size={textSize} renderAs='span' color='Black'>
                  <div className={orderLineItemNameStyle} data-testid='orderline-item-container'>
                    {t('summary:lineItemFormat', { quantity: formatItemQuantity(orderLineItem.quantity), itemName: orderLineItem.name })}
                  </div>
                </Text>
                {orderLineItem?.estimatedShipmentDate && (
                  <Text size={smallTextSize} color='Subtle'>
                    {t('common:estimatedShipping')} {formatDate(orderLineItem.estimatedShipmentDate)}
                    {'.'}
                  </Text>
                )}
                {orderLineItem.preorder && (
                  <Tag.Gray>
                    <Heading size='XS'>{t('common:preorderLabel')}</Heading>
                  </Tag.Gray>
                )}
              </div>
            </MaskedElement>
          </div>
          <div className={styles.moneyTextContainer}>
            {valueIsNotNullOrUndefined(orderLineItem.total) && (
              <div className={hideValue ? styles.visuallyInvisible : styles.moneyText}>
                <Money
                  size={sectionHeadingSize}
                  value={orderLineItem.total}
                  showFree={true}
                  loading={loading}
                  testNameSpace={`order-details-line-item-total-${index}`}
                />
              </div>
            )}
            {orderLineItem.deposit != null && (
              <div className={styles.orderLineItemDepositWrapper}>
                <div className={styles.orderLineItemDepositLabelText}>
                  <Text color='Gray50' size={smallTextSize}>
                    {t('common:depositLabel')}
                  </Text>
                </div>
                <div className={styles.orderLineItemDepositText}>
                  <Money
                    color='Gray50'
                    size={smallTextSize}
                    value={orderLineItem.deposit}
                    showFree={true}
                    loading={loading}
                    testNameSpace={`order-details-line-item-deposit-${index}`}
                  />
                </div>
              </div>
            )}
          </div>
        </Grid>
      </div>
    )
  }

  const renderOrderLineItems = (): ReactNode => {
    if (!hasItems) {
      return null
    }

    const showValue = orderDetails.reconciles

    return orderDetails.orderLineItems?.map((orderLineItem, index) => renderOrderLineItem(orderLineItem, index, isShippingOptionUpdating, showValue))
  }

  const renderSubTotal = (): ReactNode => (
    <OrderDetailsBreakdown
      label={t('summary:subTotal')}
      boldLabel={isBoldLabel}
      value={orderDetails.subTotalAmount}
      loading={isShippingOptionUpdating}
      testNameSpace='subtotal'
    />
  )

  const renderHandlingOrShipping = (): ReactNode => {
    const label = orderDetails.isPickup ? t('summary:handling') : t('summary:shipping')
    return (
      <OrderDetailsBreakdown
        label={label}
        boldLabel={isBoldLabel}
        value={orderDetails.shippingAmount}
        loading={isShippingOptionUpdating}
        testNameSpace='handlingOrShipping'
      />
    )
  }

  const renderTaxes = (): ReactNode => {
    const value = moneyToNumber(orderDetails.taxAmount) > 0 ? orderDetails.taxAmount : undefined
    return <OrderDetailsBreakdown label={t('summary:taxes')} boldLabel={isBoldLabel} value={value} loading={isShippingOptionUpdating} testNameSpace='taxes' />
  }

  const renderDiscount = (): ReactNode => {
    const value = state.started?.promotion?.discount
    if (isNullOrUndefined(value)) {
      return null
    }

    return (
      <OrderDetailsBreakdown
        label={t('summary:discountBreakdown')}
        boldLabel={true}
        value={value}
        loading={isShippingOptionUpdating}
        isDiscount={true}
        testNameSpace='discount'
      />
    )
  }

  const renderTotal = (): ReactNode => {
    if (!shouldRenderTotal) {
      return null
    }

    return (
      <OrderDetailsBreakdown
        label={t('summary:total')}
        boldLabel={true}
        boldMoney={true}
        value={summaryTotal}
        loading={isShippingOptionUpdating}
        slideMoneyIn={true}
        testNameSpace='total'
      />
    )
  }

  const renderShippingBreakdown = (): ReactNode => {
    if (!hasSelectedShippingOption) {
      return null
    }

    return (
      <DividerWrapper>
        {renderSubTotal()}
        {renderHandlingOrShipping()}
        {renderTaxes()}
        {isArcadeDesignLanguage && renderDiscount()}
      </DividerWrapper>
    )
  }

  useEffect(() => {
    const showValue = orderDetails.reconciles
    dispatch(setDoItemsReconcile(showValue))
  }, [orderDetails.reconciles])

  return (
    <div className={styles.orderDetailsContainer}>
      {renderOrderLineItems()}
      {!isArcadeDesignLanguage && (
        <>
          {renderShippingBreakdown()}
          {renderTotal()}
        </>
      )}
    </div>
  )
}

const DividerWrapper: FunctionComponent<DividerWrapperProps> = ({ children }) => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const testNameSpace = 'order-details-shipping-breakdown'

  if (isArcadeDesignLanguage) {
    return (
      <>
        <Divider kind='Hairline' margin='Half' testNameSpace={testNameSpace} />
        {children}
        <Divider kind='Hairline' margin='Half' testNameSpace={testNameSpace} />
      </>
    )
  }

  return <>{children}</>
}
